// WebSocket client of multiplayer protocol v2 (.ai/multiplayer-protocol-v2.md): `/ws/mp2`, subprotocol `po-mp2`, JSON
// frames `{t, ...}`. It says hello with the player's ticket (logged in) or the guest token kept in localStorage, and
// comes back by itself after a lost connection (the server keeps the seat 30 s and answers with `welcome.resume`).
export interface Mp2Player {
  id: string | number;
  name: string;
  flag?: string | undefined;
  away?: boolean;
}

export interface Mp2Table {
  id: string;
  code?: string;
  game: string;
  variants: string[];
  variant?: string;
  private: boolean;
  /** Two for the race; four for the card games (Makao, Kierki: the empty ones are bots when the game starts). */
  seats: (Mp2Player | null)[];
  state: 'waiting' | 'countdown' | 'playing' | 'ended';
  /** Card games: the seats whose players pressed "Start with bots" (owner 2026-10-29). */
  ready?: number[];
  since: number;
  limit?: number;
  rematch: number[];
}

export interface Mp2CatalogGame {
  /** Public rooms of one variant that may wait at once; the lobby greys "Create a room" there. */
  rooms?: number;
  game: string;
  seats: number;
  /** 'race' (the solitaires: the same deal, the higher score) or 'cards' (Makao, Kierki: turns, bots in empty seats). */
  kind: string;
  variants: { id: string; minutes: number }[];
}

export type Mp2Frame = { t: string; seq?: number; [k: string]: unknown };
type Listener = (f: Mp2Frame) => void;

const GUEST_KEY = 'po-mp-guest';
const readGuest = (): string | null => {
  try {
    return localStorage.getItem(GUEST_KEY);
  } catch {
    return null;
  }
};
const writeGuest = (token: string) => {
  try {
    localStorage.setItem(GUEST_KEY, token);
  } catch {
    // private mode: the guest gets a new name next time
  }
};

/** The v2 endpoint: the page's `<meta name="po-multiplayer">` (the mp service) with its path swapped, else the origin. */
export const multiplayerUrl2 = (): string => {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="po-multiplayer"]')?.content;
  if (meta) return meta.replace(/\/ws\/po-multiplayer\/?$/, '/ws/mp2');
  return `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws/mp2`;
};

export class Mp2Client {
  private socket: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private retry = 0;
  private retryTimer = 0;
  /** Opened at least once since connect() was called from closed: a first failure is reported, not retried. */
  private everOpened = false;
  /** Wanted open (the lobby or a table is in use); false after close(). */
  private wanted = false;
  open = false;
  /** This page sits at a table: when its line drops, its next hello takes the seat back (`resume`, the server may still
   *  hold the old half-open socket). Another device of the account never says so, and takes nothing (owner 2026-10-30). */
  seated = false;
  /** Where this lobby opening came from (stats, no personal data): said in the next hello only, so a retry is not counted again. */
  source: string | null = null;

  constructor(private readonly ticket: () => Promise<string | null>, private readonly guestLabel: () => string) {}

  on(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(f: Mp2Frame) {
    for (const fn of [...this.listeners]) fn(f);
  }

  connect(): void {
    if (!this.wanted) this.everOpened = false;
    this.wanted = true;
    if (this.socket) return;
    let s: WebSocket;
    try {
      s = new WebSocket(multiplayerUrl2(), 'po-mp2');
    } catch {
      this.wanted = false;
      this.emit({ t: 'closed', clean: false, failed: true });
      return;
    }
    this.socket = s;
    s.onopen = async () => {
      this.open = true;
      this.everOpened = true;
      this.retry = 0;
      const ticket = await this.ticket().catch(() => null);
      const guest = readGuest();
      const src = this.source;
      this.source = null;
      this.send({ t: 'hello', v: 2, device: document.body.id === 'mobile' ? 'phone' : 'desktop', ...(src ? { src } : {}), ...(ticket ? { ticket } : guest ? { guest } : {}), ...(this.seated ? { resume: true } : {}), guestLabel: this.guestLabel(), lang: document.documentElement.lang });
    };
    s.onmessage = (ev) => {
      let f: Mp2Frame;
      try {
        f = JSON.parse(String(ev.data)) as Mp2Frame;
      } catch {
        return;
      }
      if (!f || typeof f.t !== 'string') return;
      if (f.t === 'welcome' && typeof f.guest === 'string') writeGuest(f.guest);
      this.emit(f);
    };
    s.onclose = (ev) => {
      const was = this.open;
      this.open = false;
      this.socket = null;
      // the server cannot be reached at all (never opened): reported at once, no retries
      const failed = this.wanted && !this.everOpened;
      // a lost connection: try again (1 s, 2 s, 4 s ... at most 10 s); the server keeps the seat 30 s
      const retry = this.wanted && !failed && this.retry < 8;
      this.emit({ t: 'closed', clean: ev.wasClean && !this.wanted, opened: was, failed: failed || (this.wanted && !retry) });
      if (failed || !retry) this.wanted = false;
      if (retry) {
        const wait = Math.min(10_000, 1000 * 2 ** this.retry++);
        this.retryTimer = window.setTimeout(() => this.connect(), wait);
      }
    };
  }

  close(): void {
    this.wanted = false;
    window.clearTimeout(this.retryTimer);
    this.socket?.close();
  }

  send(f: Mp2Frame): void {
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(JSON.stringify(f));
  }
}
